import { useEffect, useMemo, useState } from "react";
import { EyeIcon, VendingActiveIcon } from "../../../../assets/icons";
import { Transaction, TransactionChannelMap, TransactionStatusMap } from "../../../../model/transaction";
import { formatCurrency, formatTimestamp } from "../../../../utils";
import Button from "../../../../common/components/xepp/button/Button";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import styles from "../../customer.module.scss";
import { useLazyGetTransactionsForUtilityAdminQuery } from "../../../../service/transaction-service";
import { CustomerQuery } from "../../../../model/customer/customer";
import CommonSideDialog from "../../../../common/components/side-dialog/CommonSideDialog";
import TransactionDetailsContainer from "../../../../common/components/xepp/transaction-details/TransactionDetailsContainer";

interface CustomerTransactionsProps {
	userId: string;
}

export const CustomerTransactions = ({ userId }: CustomerTransactionsProps) => {
	const [page, setPage] = useState(1);
	const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const pageSize = 15;

	const [getTransactionsForUtilityAdmin, { data, isLoading, isFetching }] = useLazyGetTransactionsForUtilityAdminQuery();

	const { items: transactions = [], count = 0 } = data ?? {};

	useEffect(() => {
		if (userId) {
			const queryParams: CustomerQuery = {
				userId,
				page,
				pageSize,
			};

			getTransactionsForUtilityAdmin(queryParams);
		}
	}, [userId, page, pageSize, getTransactionsForUtilityAdmin]);

	const handleViewTransaction = (record: Transaction) => {
		setSelectedTransaction(record);
		setIsOpen(true);
	};

	const columns: Array<ColumnType<Transaction>> = [
		{
			accessor: "id",
			header: "Transaction Type",
			render: (record: Transaction) => <span>{record.billingDetails ? "Postpaid" : "Prepaid"}</span>,
		},
		{
			accessor: "billingDetails.amount.amount",
			header: "Amount",
			render: (record: Transaction) => (
				<span>
					{record.billingDetails?.amount
						? formatCurrency(record.billingDetails?.amount.amount, { currency: record.billingDetails?.amount.currency })
						: formatCurrency(record.amount.amount, { currency: record?.amount.currency })}
				</span>
			),
		},
		{
			accessor: "type",
			header: "Channel",
			render: (record: Transaction) => <span className={styles.channelBadge}>{TransactionChannelMap[record.type]}</span>,
		},
		{
			accessor: "reference",
			header: "Reference",
			render: (record: Transaction) => (
				<span title={record.reference} className={styles.ellipsisText}>
					{record.reference}
				</span>
			),
		},
		{
			accessor: "status",
			header: "Payment Status",
			render: (record: Transaction) => (
				<span className={`${styles.statusIndicator} ${record.status ? styles[TransactionStatusMap[record.status].toLowerCase()] : ""}`}>
					{TransactionStatusMap[record.status]}
				</span>
			),
		},
		{
			header: "Date & Time",
			accessor: "createdAt",
			render: (record: Transaction) => <span>{formatTimestamp(record.billingDetails?.createdAt ?? record.createdAt)}</span>,
		},
		{
			accessor: "id",
			header: "",
			render: (record: Transaction) => (
				<Button leftIcon={<EyeIcon className={styles.icon} />} variant="text" text="View" onClick={() => handleViewTransaction(record)} />
			),
		},
	];

	return (
		<div className={styles.historyContainer}>
			{isOpen && (
				<CommonSideDialog onCloseDialog={() => setIsOpen(!isOpen)}>
					{selectedTransaction?.billingDetails || selectedTransaction?.vendingDetails ? (
						// Same container as VendingHistory/PostpaidHistory: fetches the full
						// transaction by reference and provides the timeline, error details and
						// the revend / notify-billing / verify actions.
						<TransactionDetailsContainer
							reference={selectedTransaction.reference}
							transactionType={selectedTransaction.billingDetails ? "billing" : "vending"}
							onClose={() => setIsOpen(false)}
						/>
					) : (
						// Fallback for transactions without specific details
						<div>
							<p>Transaction details not available</p>
							<button onClick={() => setIsOpen(false)}>Close</button>
						</div>
					)}
				</CommonSideDialog>
			)}
			<DataTable<Transaction>
				data={transactions ?? []}
				loading={isLoading}
				isUpdating={isFetching}
				columns={columns}
				// headerActions={headerActions}
				emptyComponent={<EmptyState icon={<VendingActiveIcon />} title="No transaction history data" />}
			/>
			<TablePagination isLoading={isFetching} totalItems={count} itemsPerPage={pageSize} currentPage={page} onPageChange={setPage} />
		</div>
	);
};
